import { useUnit } from 'effector-react'

import { $isCreatorProcessCompleted } from '@/models/creators.ts'
import { VideoCarouselSkeleton } from '@/modules/statistics/social/ui/Carousel/CarouselSkeleton.tsx'
import { fallbackToEmpty } from '@/utils/fallbacks.ts'

import type { SocialData } from '../hooks/use-social-data.ts'
import { SocialAccountSidebar } from '../ui/Sidebar'
import { SocialAccountWidget } from '../ui/Widget'
import { NoSocialAccountView } from './Errors/NoSocialAccountView.tsx'

interface SocialPageViewProps {
  data: SocialData
  query: string
}

export const SocialPageView = ({ data, query }: SocialPageViewProps) => {
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)

  const { accounts, chartData, summarizedData, isFetching } = data

  if (isFetching || !isCreatorProcessCompleted) {
    return (
      <div className="grid w-full grid-cols-1 gap-6">
        <div className="flex flex-col gap-6 xl:flex-row">
          <div className="flex animate-pulse flex-col rounded-lg bg-skeleton p-40 xl:max-w-sm" />
          <div className="flex w-full animate-pulse flex-col rounded-lg bg-skeleton p-40" />
        </div>
        <div className="mb-12 flex flex-col gap-10">
          <VideoCarouselSkeleton />
        </div>
      </div>
    )
  }

  const filteredViews = fallbackToEmpty(
    accounts?.views.filter(
      (account) =>
        account.account.includes(query) || account.name?.includes(query),
    ),
  )

  if (filteredViews.length === 0 || !chartData) {
    return <NoSocialAccountView />
  }

  return (
    <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-1">
      <SocialAccountSidebar
        summarizedData={summarizedData}
        chartData={chartData}
      />
      <div className="flex flex-col gap-10">
        {filteredViews.map((account, index) => (
          <SocialAccountWidget
            account={account}
            performanceState={
              accounts?.['performance-states']?.[account.account]
            }
            key={index}
          />
        ))}
      </div>
    </div>
  )
}
